import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ShieldOff, Download, Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import {
  getMfaStatus,
  setupMfa,
  enableMfa,
  disableMfa,
  regenerateMfaBackupCodes,
  apiErrorMessage,
} from '@/lib/api';
import { MFA_CARD } from '@/constants/testIds';

const cardClass = 'max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8';
const fieldClass = 'bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] h-10 focus-visible:ring-[hsl(var(--lxra-gold))]';

function BackupCodesReveal({ codes, onDone }) {
  const [ack, setAck] = useState(false);

  const handleDownload = () => {
    const blob = new Blob([`LXRA Studios — Two-Factor Backup Codes\nEach code can be used once if you lose access to your authenticator app.\n\n${codes.join('\n')}\n`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lxra-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Backup codes copied to clipboard.');
    } catch {
      toast.error('Could not copy — please save them manually.');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-[hsl(var(--lxra-muted))]">
        Save these one-time backup codes somewhere safe. Each can be used once to sign in if you lose access to
        your authenticator app. They won&apos;t be shown again.
      </p>
      <div
        data-testid={MFA_CARD.backupCodesList}
        className="grid grid-cols-2 gap-2 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-4 font-mono text-sm text-[hsl(var(--lxra-fg))]"
      >
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleDownload}
          data-testid={MFA_CARD.backupCodesDownload}
          variant="outline"
          className="h-9 flex-1 rounded-lg border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] hover:bg-[hsl(var(--lxra-gold))]/10 text-xs uppercase tracking-[0.08em]"
        >
          <Download className="h-3.5 w-3.5 mr-2" />
          Download
        </Button>
        <Button
          type="button"
          onClick={handleCopy}
          variant="outline"
          className="h-9 flex-1 rounded-lg border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-white/5 text-xs uppercase tracking-[0.08em]"
        >
          <Copy className="h-3.5 w-3.5 mr-2" />
          Copy
        </Button>
      </div>
      <label className="flex items-start gap-2 text-xs text-[hsl(var(--lxra-muted))] cursor-pointer">
        <Checkbox
          checked={ack}
          onCheckedChange={(v) => setAck(!!v)}
          data-testid={MFA_CARD.backupCodesAckCheckbox}
          className="mt-0.5"
        />
        I&apos;ve saved my backup codes somewhere safe.
      </label>
      <Button
        type="button"
        disabled={!ack}
        onClick={onDone}
        data-testid={MFA_CARD.backupCodesDoneButton}
        className="w-full h-10 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-50 text-xs uppercase tracking-[0.1em] font-semibold"
      >
        Done
      </Button>
    </div>
  );
}

// Full 2FA (TOTP) management card: works for any logged-in account (admin,
// photographer, or client) since the backend infers which collection to use
// from the session. Optional toggle, per LXRA's chosen policy — nobody is
// forced into this, but everyone can turn it on from their own settings.
export const MfaSetupCard = () => {
  const [status, setStatus] = useState(null); // { enabled, backup_codes_remaining }
  const [step, setStep] = useState('status'); // status | qr | backup-codes
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [revealedCodes, setRevealedCodes] = useState(null);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  const [regenOpen, setRegenOpen] = useState(false);
  const [regenPassword, setRegenPassword] = useState('');
  const [regenError, setRegenError] = useState('');
  const [regenLoading, setRegenLoading] = useState(false);

  const refreshStatus = async () => {
    try {
      const s = await getMfaStatus();
      setStatus(s);
    } catch {
      setStatus({ enabled: false, backup_codes_remaining: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const handleStartSetup = async () => {
    setSetupError('');
    try {
      const data = await setupMfa();
      setSetupData(data);
      setCode('');
      setStep('qr');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not start 2FA setup.'));
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setSetupError('');
    setVerifying(true);
    try {
      const data = await enableMfa(code.trim());
      setRevealedCodes(data.backup_codes);
      setStep('backup-codes');
    } catch (err) {
      setSetupError(apiErrorMessage(err, 'Incorrect code. Please try again.'));
    } finally {
      setVerifying(false);
    }
  };

  const handleBackupCodesDone = async () => {
    setStep('status');
    setSetupData(null);
    setRevealedCodes(null);
    toast.success('Two-factor authentication is now on.');
    await refreshStatus();
  };

  const handleDisable = async () => {
    setDisableError('');
    setDisableLoading(true);
    try {
      await disableMfa(disablePassword, disableCode.trim());
      setDisableOpen(false);
      setDisablePassword('');
      setDisableCode('');
      toast.success('Two-factor authentication has been turned off.');
      await refreshStatus();
    } catch (err) {
      setDisableError(apiErrorMessage(err, 'Could not disable 2FA.'));
    } finally {
      setDisableLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenError('');
    setRegenLoading(true);
    try {
      const data = await regenerateMfaBackupCodes(regenPassword);
      setRegenOpen(false);
      setRegenPassword('');
      setRevealedCodes(data.backup_codes);
      setStep('backup-codes');
    } catch (err) {
      setRegenError(apiErrorMessage(err, 'Could not regenerate backup codes.'));
    } finally {
      setRegenLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={cardClass} data-testid={MFA_CARD.root}>
        <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--lxra-muted))]" />
      </div>
    );
  }

  return (
    <div className={cardClass} data-testid={MFA_CARD.root}>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-xl text-[hsl(var(--lxra-fg))]">Two-Factor Authentication</h3>
        {status?.enabled ? (
          <Badge data-testid={MFA_CARD.statusEnabledBadge} className="bg-[hsl(var(--lxra-gold)/0.14)] text-[hsl(var(--lxra-cream))] border border-[hsl(var(--lxra-gold)/0.35)]">
            <ShieldCheck className="h-3 w-3 mr-1" /> Enabled
          </Badge>
        ) : (
          <Badge data-testid={MFA_CARD.statusDisabledBadge} variant="secondary">
            <ShieldOff className="h-3 w-3 mr-1" /> Off
          </Badge>
        )}
      </div>
      <p className="text-sm text-[hsl(var(--lxra-muted))] mb-6">
        Add an authenticator app (Google Authenticator, Authy, 1Password) as a second step when signing in.
      </p>

      {step === 'status' && !status?.enabled && (
        <Button
          type="button"
          onClick={handleStartSetup}
          data-testid={MFA_CARD.startSetupButton}
          className="h-10 px-5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold"
        >
          Set Up Two-Factor
        </Button>
      )}

      {step === 'status' && status?.enabled && (
        <div className="space-y-4">
          <p data-testid={MFA_CARD.backupCodesRemaining} className="text-xs text-[hsl(var(--lxra-muted))]">
            {status.backup_codes_remaining} backup code{status.backup_codes_remaining === 1 ? '' : 's'} remaining.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => setRegenOpen(true)}
              data-testid={MFA_CARD.regenerateButton}
              variant="outline"
              className="h-9 rounded-lg border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-white/5 text-xs uppercase tracking-[0.08em]"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
              Regenerate Backup Codes
            </Button>
            <Button
              type="button"
              onClick={() => setDisableOpen(true)}
              data-testid={MFA_CARD.disableButton}
              variant="outline"
              className="h-9 rounded-lg border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-xs uppercase tracking-[0.08em]"
            >
              Turn Off 2FA
            </Button>
          </div>
        </div>
      )}

      {step === 'qr' && setupData && (
        <div className="space-y-4">
          <div className="flex flex-col items-center gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#141416] p-5">
            <img src={setupData.qr_code_base64} alt="Scan this QR code with your authenticator app" data-testid={MFA_CARD.qrImage} className="h-40 w-40 rounded-lg bg-white p-2" />
            <p className="text-[11px] text-[hsl(var(--lxra-muted))]">Can&apos;t scan? Enter this code manually:</p>
            <code data-testid={MFA_CARD.secretText} className="text-xs text-[hsl(var(--lxra-fg))] bg-black/30 rounded px-2 py-1 tracking-wider break-all">
              {setupData.secret}
            </code>
          </div>
          <form onSubmit={handleVerify} className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Enter the 6-digit code</Label>
              <Input
                required
                autoFocus
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid={MFA_CARD.verifyCodeInput}
                className={`${fieldClass} text-center tracking-[0.2em]`}
              />
            </div>
            {setupError && <p className="text-xs text-red-400" data-testid={MFA_CARD.setupErrorText}>{setupError}</p>}
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={verifying || !code.trim()}
                data-testid={MFA_CARD.verifyCodeSubmit}
                className="h-10 flex-1 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify & Enable'}
              </Button>
              <Button
                type="button"
                onClick={() => setStep('status')}
                data-testid={MFA_CARD.cancelSetupButton}
                variant="outline"
                className="h-10 rounded-lg border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] hover:bg-white/5 text-xs uppercase tracking-[0.1em]"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {step === 'backup-codes' && revealedCodes && <BackupCodesReveal codes={revealedCodes} onDone={handleBackupCodesDone} />}

      {/* Disable 2FA */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent data-testid={MFA_CARD.disableDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <DialogHeader>
            <DialogTitle>Turn Off Two-Factor Authentication</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">
              Confirm your password and a current code to disable 2FA on this account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Password</Label>
              <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} data-testid={MFA_CARD.disablePasswordInput} className={fieldClass} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Authenticator or backup code</Label>
              <Input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid={MFA_CARD.disableCodeInput} className={fieldClass} />
            </div>
            {disableError && <p className="text-xs text-red-400" data-testid={MFA_CARD.disableErrorText}>{disableError}</p>}
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={disableLoading || !disablePassword || !disableCode}
              onClick={handleDisable}
              data-testid={MFA_CARD.disableConfirmButton}
              className="h-10 rounded-lg bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
            >
              {disableLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Turn Off 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate backup codes */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent data-testid={MFA_CARD.regenerateDialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
          <DialogHeader>
            <DialogTitle>Regenerate Backup Codes</DialogTitle>
            <DialogDescription className="text-[hsl(var(--lxra-muted))]">
              Your old backup codes will stop working immediately. Confirm your password to generate a fresh set.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Password</Label>
              <Input type="password" value={regenPassword} onChange={(e) => setRegenPassword(e.target.value)} data-testid={MFA_CARD.regeneratePasswordInput} className={fieldClass} />
            </div>
            {regenError && <p className="text-xs text-red-400" data-testid={MFA_CARD.regenerateErrorText}>{regenError}</p>}
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={regenLoading || !regenPassword}
              onClick={handleRegenerate}
              data-testid={MFA_CARD.regenerateConfirmButton}
              className="h-10 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
            >
              {regenLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
